import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { useMobile } from "src/hooks/useMobile";
import { FormParser } from "../FormParser";
import { StartScreenView } from "../StartScreenView";
import { S_AUTH_CHANGE_VIEW } from "./AuthSignals";

const MIN_PASSWORD_LENGTH = "password".length;
const LOGIN_REDIRECT_DELAY = 2000;

const form = {
	password: "",
	$__password_type: "password",
	get $__password_label() { return msg("newPassword"); },
	confirm_password: "",
	$__confirm_password_type: "password",
	get $__confirm_password_label() { return msg("confirmPassword"); },
};

type TForcePasswordChangeForm = typeof form;
type TForcePasswordChangeSubmit = {
	data: TForcePasswordChangeForm;
	error?: IError;
};

const getForm = () => ({ ...form });

const validate = (data: TForcePasswordChangeForm): IError | null => {
	if (!data.password || !data.confirm_password) {
		return {
			code: 0,
			message: msg("passwordAndConfirmationAreRequired"),
		};
	}

	if (data.password !== data.confirm_password) {
		return {
			code: 0,
			message: msg("yourPasswordsDonTMatchPleaseCheckAndTryAgain"),
		};
	}

	if (data.password.length < MIN_PASSWORD_LENGTH) {
		return {
			code: 0,
			message: msg("passwordMustBeAtLeast8CharactersLong"),
		};
	}

	return null;
};

export const ForcePasswordChange = () => {
	useLocale();
	const [busy, setBusy] = useState(false);
	const [completed, setCompleted] = useState<boolean>();
	const { isMobile, isTablet } = useMobile();

	useEffect(() => {
		if (!completed) return;

		const timerId = window.setTimeout(() => {
			S_AUTH_CHANGE_VIEW.invoke("login");
		}, LOGIN_REDIRECT_DELAY);

		return () => window.clearTimeout(timerId);
	}, [completed]);

	return (
		<StartScreenView busy={busy}>
			<Box
				sx={{
					width: isMobile ? "100%" : undefined,
					overflow: isMobile ? "auto" : undefined,
					display: "flex",
					height: "100%",
					minHeight: isMobile ? undefined : "700px",
					borderStartStartRadius: isMobile ? "0" : "12px",
					borderEndStartRadius: isMobile ? "0" : "12px",
					borderEndEndRadius: isTablet ? "12px" : "0",
					borderStartEndRadius: isTablet ? "12px" : "0",
					backgroundColor: "rgba(255,255,255,1)",
				}}>
				<Box
					sx={{
						padding: isMobile ? "34px 26px" : "60px",
						minWidth: isMobile ? "0" : "500px",
						width: isMobile ? "100%" : "auto",
						display: "flex",
						margin: isMobile ? "auto" : undefined,
						maxWidth: isMobile ? "500px" : undefined,
						flexDirection: "column",
					}}>
					{completed ? (
						<>
							<Typo capitalize sx={{ fontSize: "26px", fontWeight: 400 }}>{msg("passwordUpdatedSuccessfully")}<br />{msg("pleaseSignInWithYourNew")}<br />{msg("credentials")}</Typo>
							<Spacer size="14px" />
							<TypoCaption sx={{ color: "rgba(0,0,0,0.6)" }}>{msg("redirectingToTheLoginPage")}</TypoCaption>
						</>
					) : (
						<Typo capitalize sx={{ fontSize: "26px", fontWeight: 400 }}>{msg("yourPasswordMustBeChanged")}<br />{msg("beforeYouCanContinue")}</Typo>
					)}
					<Spacer size="40px" />

					{!completed && (
						<>
							<TypoCaption sx={{ color: "rgba(0,0,0,0.6)" }}>{msg("enterANewPasswordAndConfirmItToFinishSigningIn")}</TypoCaption>
							<Spacer size="20px" />
							<FormParser<TForcePasswordChangeForm>
								key={"forcePasswordChangeForm"}
								ignoreObserver={true}
								secondaryButton={null}
								fullSizeButtons={true}
								primaryButton={{ title: msg("changePassword"), variant: "primary", sx: { flexGrow: "1" } }}
								onSubmit={async (data: TForcePasswordChangeForm): Promise<TForcePasswordChangeSubmit> => {
									const validationError = validate(data);

									if (validationError) {
										return { data, error: validationError };
									}

									setBusy(true);
									const response = await GD.REQ_AUTH_CHANGE_PASSWORD.request({
										password: data.password,
									});
									setBusy(false);

									if (response.error) {
										return { data, error: response.error };
									}

									setCompleted(true);
									return { data };
								}}
								onRequest={async (): Promise<TForcePasswordChangeForm> => getForm()}
							/>
						</>
					)}

					<Box
						sx={{
							display: "flex",
							gap: "5px",
							alignItems: "center",
							margin: "25px 0",
						}}>
						<Box
							sx={{
								border: "1px solid rgba(0,0,0,0.14)",
								width: "100%",
							}}
						/>
						<TypoCaption italic>{msg("or")}</TypoCaption>
						<Box
							sx={{
								border: "1px solid rgba(0,0,0,0.14)",
								width: "100%",
							}}
						/>
					</Box>

					<Button
						sx={{
							width: "auto",
						}}
						variant={completed ? "primary" : "secondary"}
						onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")}>{msg("signIn")}</Button>
				</Box>
			</Box>
		</StartScreenView>
	);
};
